import { Accordion, Alert, Badge, Group, Loader, Text } from "@mantine/core";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { Role } from "./api";
import { RoleEditor } from "./RoleEditor";
import { useAgentRoles } from "./useAgentRoles";
import { agentRoleErrorText } from "./refusalText";

// Список встроенных ролей как inline-аккордеон: клик по заголовку роли
// разворачивает её промпт (<RoleEditor>) прямо под карточкой, а не в
// отдельной панели внизу страницы. Это даёт линейное чтение всех ролей
// сверху вниз без прыжков. В конце развёрнутого промпта — ссылки на
// связанные роли (cross-references); переход по ссылке разворачивает и
// подскролливает соответствующую роль.

// Смысловые связи между промптами ролей (они уже ссылаются друг на
// друга по содержанию: coder ↔ reviewer, orchestrator → coder/infra,
// writer → coder, infra → coder). UI делает эти связи навигируемыми.
const ROLE_LINKS: Record<string, readonly string[]> = {
  orchestrator: ["coder", "infra"],
  coder: ["reviewer"],
  reviewer: ["coder"],
  writer: ["coder"],
  infra: ["coder"],
};

// Префикс id у Accordion.Item — по нему navigate() подскролливает
// целевую роль в зону видимости.
function itemDomId(roleName: string): string {
  return `agent-role-${roleName}`;
}

function RoleLabel({
  role,
  t,
}: {
  readonly role: Role;
  readonly t: TFunction;
}): ReactElement {
  const hasAnyOverride =
    role.prompt_override_source !== null || role.has_model_override;
  return (
    <Group justify="space-between" wrap="nowrap" pr="md">
      <Text fw={500}>
        {t(`agentRoles.role.${role.role_name}`, {
          defaultValue: role.role_name,
        })}
      </Text>
      <Group gap="xs">
        {role.prompt_override_source !== null ? (
          <Badge color="grape">
            {t(`agentRoles.source.${role.prompt_override_source}`)}
          </Badge>
        ) : null}
        {role.has_model_override ? (
          <Badge color="indigo">{t("agentRoles.model.overrideBadge")}</Badge>
        ) : null}
        {!hasAnyOverride ? (
          <Badge variant="light" color="gray">
            {t("agentRoles.usingDefault")}
          </Badge>
        ) : null}
      </Group>
    </Group>
  );
}

export function AgentRolesList(): ReactElement {
  const { t } = useTranslation();
  const roles = useAgentRoles();
  const [opened, setOpened] = useState<string | null>(null);

  if (roles.isLoading) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          {t("agentRoles.loading")}
        </Text>
      </Group>
    );
  }

  if (roles.isError) {
    return (
      <Alert color="red" title={t("agentRoles.loadError")}>
        {agentRoleErrorText(roles.error, t)}
      </Alert>
    );
  }

  const items = roles.data ?? [];
  if (items.length === 0) {
    return <Text c="dimmed">{t("agentRoles.empty")}</Text>;
  }

  const known = new Set(items.map((role) => role.role_name));

  // Переход по cross-ссылке: разворачиваем целевую роль и подскролливаем
  // её к началу. Ссылки строятся только на роли из items (см. фильтр по
  // known ниже), поэтому Accordion.Item с этим id гарантированно
  // отрисован — getElementById не вернёт null.
  const navigateToRole = (roleName: string): void => {
    setOpened(roleName);
    document
      .getElementById(itemDomId(roleName))!
      .scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Accordion
      variant="separated"
      radius="md"
      value={opened}
      onChange={setOpened}
    >
      {items.map((role) => (
        <Accordion.Item
          key={role.role_name}
          value={role.role_name}
          id={itemDomId(role.role_name)}
        >
          <Accordion.Control>
            <RoleLabel role={role} t={t} />
          </Accordion.Control>
          <Accordion.Panel>
            <RoleEditor
              roleName={role.role_name}
              relatedRoles={(ROLE_LINKS[role.role_name] ?? []).filter((name) =>
                known.has(name),
              )}
              onNavigateToRole={navigateToRole}
            />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}
